import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { FileText, Clock, CheckCircle, XCircle, AlertCircle, Eye, Pencil } from 'lucide-react';
import { UploadVersionDialog } from './UploadVersionDialog';
import { VersionApprovalDialog } from './VersionApprovalDialog';
import { RejectionReasonDialog } from './RejectionReasonDialog';
import { ViewDocumentLink } from './ViewDocumentLink';
import { useIsAdmin } from '@/hooks/useUserRole';
import { formatDisplayName } from '@/lib/formatName';
import { supabase } from '@/integrations/supabase/client';
import type { PolicyVersion } from '@/hooks/usePolicy';

interface VersionHistoryTabProps {
  policyId: string;
  policyName: string;
  versions: PolicyVersion[];
  isLoading: boolean;
  policyStatus?: string;
}

function VersionStatusBadge({ status }: { status?: string }) {
  switch (status) {
    case 'pending_approval':
      return (
        <Badge variant="outline" className="gap-1 border-amber-300 bg-amber-50 text-amber-700 dark:border-amber-700 dark:bg-amber-950 dark:text-amber-400">
          <Clock className="h-3 w-3" />
          Pending
        </Badge>
      );
    case 'approved':
      return (
        <Badge variant="outline" className="gap-1 border-emerald-300 bg-emerald-50 text-emerald-700 dark:border-emerald-700 dark:bg-emerald-950 dark:text-emerald-400">
          <CheckCircle className="h-3 w-3" />
          Approved
        </Badge>
      );
    case 'rejected':
      return (
        <Badge variant="outline" className="gap-1 border-red-300 bg-red-50 text-red-700 dark:border-red-700 dark:bg-red-950 dark:text-red-400">
          <XCircle className="h-3 w-3" />
          Rejected
        </Badge>
      );
    default:
      // Legacy versions without status - assume approved
      return (
        <Badge variant="outline" className="gap-1 border-emerald-300 bg-emerald-50 text-emerald-700 dark:border-emerald-700 dark:bg-emerald-950 dark:text-emerald-400">
          <CheckCircle className="h-3 w-3" />
          Approved
        </Badge>
      );
  }
}

export function VersionHistoryTab({ policyId, policyName, versions, isLoading, policyStatus }: VersionHistoryTabProps) {
  const { isAdmin } = useIsAdmin();
  const navigate = useNavigate();
  const [selectedVersion, setSelectedVersion] = useState<PolicyVersion | null>(null);
  const [viewingRejection, setViewingRejection] = useState<PolicyVersion | null>(null);

  // Query for in-progress composer draft for this policy
  const { data: activeDraft } = useQuery({
    queryKey: ['policy-active-draft', policyId],
    queryFn: async () => {
      const { data } = await supabase
        .from('policy_drafts')
        .select('id, created_by, updated_at, generation_mode, profiles:created_by(full_name)')
        .eq('policy_id', policyId)
        .eq('status', 'draft')
        .order('updated_at', { ascending: false })
        .limit(1)
        .maybeSingle();
      return data;
    },
    enabled: isAdmin,
  });

  // Find pending version for the banner
  const pendingVersion = useMemo(() => {
    return versions.find(v => v.version_status === 'pending_approval');
  }, [versions]);

  if (isLoading) {
    return (
      <div className="space-y-3">
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-10 w-full" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Pending Approval Banner */}
      {pendingVersion && isAdmin && (
        <div className="flex items-center justify-between gap-4 rounded-lg border border-status-pending bg-status-pending/10 p-4">
          <div className="flex items-center gap-3">
            <AlertCircle className="h-5 w-5 text-status-pending shrink-0" />
            <div>
              <p className="font-medium text-status-pending">
                Version {pendingVersion.version_number} pending approval
              </p>
              <p className="text-sm text-muted-foreground">
                Uploaded by {formatDisplayName(pendingVersion.created_by_profile?.full_name ?? 'Unknown', pendingVersion.created_by_profile?.credentials)} on{' '}
                {format(new Date(pendingVersion.created_at), 'MMM d, yyyy')}
                {pendingVersion.is_substantive && ' • Substantive update'}
              </p>
            </div>
          </div>
          <Button 
            size="sm" 
            onClick={() => setSelectedVersion(pendingVersion)}
            className="shrink-0"
          >
            Review
          </Button>
        </div>
      )}

      {isAdmin && (
        <div className="flex justify-end gap-2">
          <UploadVersionDialog policyId={policyId} policyName={policyName} />
        </div>
      )}

      {versions.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <FileText className="mb-3 h-10 w-10 text-muted-foreground/50" />
          <p className="text-muted-foreground">No versions uploaded yet.</p>
          <p className="text-sm text-muted-foreground/70">
            {isAdmin
              ? 'Click "Upload New Version" to add the first document.'
              : 'Policy versions will appear here once documents are uploaded.'}
          </p>
        </div>
      ) : (
        <div className="rounded-md border bg-card">
          <Table>
            <TableHeader className="bg-[hsl(var(--table-header))]">
              <TableRow>
                <TableHead className="w-[80px]">Version</TableHead>
                <TableHead className="w-[90px]">Lifecycle</TableHead>
                <TableHead className="w-[100px]">Status</TableHead>
                <TableHead>Uploaded By</TableHead>
                <TableHead>Date</TableHead>
                <TableHead>Type</TableHead>
                <TableHead className="w-[80px]">Document</TableHead>
                {isAdmin && <TableHead className="w-[80px]">Action</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {/* Synthetic draft row */}
              {activeDraft && isAdmin && !pendingVersion && (
                <TableRow className="bg-muted/30">
                  <TableCell className="font-medium text-muted-foreground italic">Draft</TableCell>
                  <TableCell>
                    <Badge variant="outline" className="gap-1 border-muted bg-muted text-muted-foreground text-[10px]">
                      In Progress
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline" className="gap-1 border-border bg-muted text-muted-foreground">
                      Draft
                    </Badge>
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {(activeDraft.profiles as any)?.full_name ?? '—'}
                  </TableCell>
                  <TableCell className="text-muted-foreground whitespace-nowrap">
                    {format(new Date(activeDraft.updated_at), 'MMM d, yyyy')}
                  </TableCell>
                  <TableCell>—</TableCell>
                  <TableCell>—</TableCell>
                  {isAdmin && (
                    <TableCell>
                      <Button
                        variant="outline"
                        size="sm"
                        className="border-teal-500 text-teal-600 hover:bg-teal-50 dark:border-teal-600 dark:text-teal-400 dark:hover:bg-teal-950"
                        onClick={() => {
                          const url = activeDraft.generation_mode === 'revision'
                            ? `/composer?policy_id=${policyId}&mode=update`
                            : `/composer?policy_id=${policyId}`;
                          navigate(url);
                        }}
                      >
                        <Pencil className="h-3.5 w-3.5 mr-1" /> Edit in Policy Builder
                      </Button>
                    </TableCell>
                  )}
                </TableRow>
              )}
              {versions.map((version) => (
                <TableRow 
                  key={version.id}
                  className={version.version_status === 'rejected' ? 'opacity-60' : undefined}
                >
                  <TableCell className="font-medium">
                    v{version.version_number}
                   </TableCell>
                  <TableCell>
                    {version.version_number === versions[0]?.version_number ? (
                      <Badge variant="outline" className="gap-1 border-emerald-300 bg-emerald-50 text-emerald-700 dark:border-emerald-700 dark:bg-emerald-950 dark:text-emerald-400 text-[10px]">
                        Active
                      </Badge>
                    ) : (
                      <Badge variant="outline" className="gap-1 border-muted bg-muted text-muted-foreground text-[10px]">
                        Historical
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell>
                    <VersionStatusBadge status={version.version_status} />
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {version.created_by_profile ? formatDisplayName(version.created_by_profile.full_name, version.created_by_profile.credentials) : '—'}
                  </TableCell>
                  <TableCell className="text-muted-foreground whitespace-nowrap">
                    <span className="md:hidden">{format(new Date(version.created_at), 'MMM d, yyyy')}</span>
                    <span className="hidden md:block">{format(new Date(version.created_at), 'MMM d, yyyy h:mm a')}</span>
                  </TableCell>
                  <TableCell>
                    {version.is_substantive ? (
                      <Badge variant="secondary" className="text-xs">Substantive</Badge>
                    ) : (
                      <span className="text-sm text-muted-foreground">Minor</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <ViewDocumentLink 
                      fileUrl={version.file_url} 
                      pdfUrl={version.pdf_url}
                      policyId={policyId}
                      title={`${policyName} - v${version.version_number}`} 
                    />
                  </TableCell>
                  {isAdmin && (
                    <TableCell>
                      {version.version_status === 'pending_approval' && (
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => setSelectedVersion(version)}
                        >
                          Review
                        </Button>
                      )}
                      {version.version_status === 'rejected' && (
                        <Button 
                          variant="ghost" 
                          size="sm"
                          className="text-primary hover:text-primary/80"
                          onClick={() => setViewingRejection(version)}
                        >
                          <Eye className="h-3.5 w-3.5 mr-1" />
                          View Reason
                        </Button>
                      )}
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Approval Dialog */}
      {selectedVersion && (
        <VersionApprovalDialog
          open={!!selectedVersion}
          onOpenChange={(open) => !open && setSelectedVersion(null)}
          version={selectedVersion}
          policyId={policyId}
          policyName={policyName}
        />
      )}

      {/* Rejection Reason Dialog */}
      {viewingRejection && (
        <RejectionReasonDialog
          open={!!viewingRejection}
          onOpenChange={(open) => !open && setViewingRejection(null)}
          version={viewingRejection}
          policyId={policyId}
          policyName={policyName}
        />
      )}
    </div>
  );
}
